/**
 * Pure presentation helpers for Ano Sync cards + the Control Tower panel.
 * No React here — labels + semantic-token class strings only, so the mapping
 * is unit-testable. Icon component maps live in the .tsx files (top-level
 * static maps, per the static-components rule).
 */
import type {
  SyncEventSource,
  SyncEventStatus,
  SyncEventType,
} from "@ano/shared/sync/sync-event";
import type { ControlTowerTestStatus } from "@ano/shared/sync/control-tower";

export const SOURCE_LABELS: Record<SyncEventSource, string> = {
  claude_code: "Claude Code",
  codex: "Codex",
  cursor: "Cursor",
  github: "GitHub",
  linear: "Linear",
  terminal: "Terminal",
  manual: "Manual",
  system: "System",
};

export const EVENT_TYPE_LABELS: Record<SyncEventType, string> = {
  work_started: "Work started",
  progress: "Progress",
  decision: "Decision",
  blocker: "Blocker",
  question: "Question",
  tests: "Tests",
  review_requested: "Review requested",
  completed: "Completed",
  failed: "Failed",
};

/** Foreground text token for a status (semantic tokens only — no hex). */
export function statusTextClass(status: SyncEventStatus): string {
  switch (status) {
    case "success":
      return "text-success";
    case "warning":
      return "text-warning";
    case "error":
      return "text-destructive";
    case "info":
    default:
      return "text-info";
  }
}

/** Muted-bg pill class for a status (the in-codebase status-pill pattern). */
export function statusPillClass(status: SyncEventStatus): string {
  switch (status) {
    case "success":
      return "bg-success-muted text-success";
    case "warning":
      return "bg-warning-muted text-warning";
    case "error":
      return "bg-destructive-muted text-destructive";
    case "info":
    default:
      return "bg-info-muted text-info";
  }
}

/** Pill class for the Control Tower's rolled-up test status. */
export function testStatusPillClass(status: ControlTowerTestStatus): string {
  switch (status) {
    case "passed":
      return "bg-success-muted text-success";
    case "failed":
      return "bg-destructive-muted text-destructive";
    case "mixed":
      return "bg-warning-muted text-warning";
    case "unknown":
    default:
      return "bg-foreground/10 text-muted-foreground";
  }
}

export const TEST_STATUS_LABELS: Record<ControlTowerTestStatus, string> = {
  passed: "Tests passing",
  failed: "Tests failing",
  mixed: "Tests mixed",
  unknown: "Tests unknown",
};
